/* cf-quote-editor.js - Click Fencing, 2026-08-15
 *
 * Lets a customer adjust quantities on their saved quote before checkout: add a panel,
 * remove a post, drop a latch. Runs on /pages/quote-review only.
 *
 * Works off the saved quote in localStorage (<calc>_active_quote / <calc>_quotes), whose
 * breakdown array already carries {category,item,sku,quantity,unitPrice,totalPrice,
 * variantId,section,colour}. Nothing here touches the four solvers, so the drawn fence
 * never changes - adjustments sit on top of the solved design.
 *
 * Every change is recorded as quote.cfAdjustments and surfaced three ways: on screen, as
 * line-item properties on the Shopify order, and as a summary property listing removals
 * (a removed line cannot carry its own property - it is gone from the cart).
 *
 * Lines with variantId === null are tagged "quoted only". Since 2026-08-15
 * cf-real-line-items.js resolves most of them through CF_VARIANT(sku), so far fewer lines
 * carry the tag than the name suggests.
 *
 * Self-installing, idempotent, every path wrapped so a display fault can never block
 * checkout. Same shape as cf-calc-fixes.js / ullc-catalogue-fix.js.
 */
(function () {
  'use strict';
  if (window.__cfQuoteEditorInstalled) return;
  window.__cfQuoteEditorInstalled = true;

  var CALCS = ['ullc', 'blade', 'flattop', 'perfmesh'];
  var MONEY = function (n) {
    return '$' + (Math.round((Number(n) || 0) * 100) / 100).toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  /* WHICH quote is on screen. The original rule was "first calculator that has any saved
     quote", which was wrong twice over: a customer with an old blade quote who then built
     a mesh one got the BLADE quote bound to the editor, and switching quote pills left the
     previous quote in place. Note <calc>_active_quote holds the active quote NUMBER (blade
     stores "9"), not the quote object, so it has to be matched back against <calc>_quotes.
     cf-gst-display.js and cf-real-line-items.js resolve the same way. */
  function candidates() {
    var out = [];
    for (var i = 0; i < CALCS.length; i++) {
      var c = CALCS[i], active = null;
      try { active = JSON.parse(localStorage.getItem(c + '_active_quote') || 'null'); } catch (e) {}
      if (active && active.breakdown && active.breakdown.length) {
        out.push({ key: c + '_active_quote', idx: -1, quote: active, calc: c, isActive: true });
      }
      var activeNum = (active !== null && typeof active !== 'object') ? Number(active) : null;
      var arr = [];
      try { arr = JSON.parse(localStorage.getItem(c + '_quotes') || '[]') || []; } catch (e) {}
      if (Object.prototype.toString.call(arr) === '[object Array]') {
        for (var j = 0; j < arr.length; j++) {
          var q = arr[j];
          if (!q || !q.breakdown || !q.breakdown.length) continue;
          out.push({ key: c + '_quotes', idx: j, quote: q, calc: c,
                     isActive: activeNum !== null && Number(q.quoteNum) === activeNum });
        }
      }
    }
    return out;
  }

  function readQuote() {
    var list = candidates();
    if (!list.length) return null;
    /* Match the quote quote-review actually rendered (published by hydrate). The pill
       text below stopped matching once a "×" was appended to it, and editing the wrong
       quote writes to it. In a mixed fence job this file is hidden entirely. */
    try {
      var vq = window.__cfViewedQuote;
      if (vq && !vq.isJob) {
        for (var k = 0; k < list.length; k++) {
          var c = list[k].quote;
          if (String(c.calcType || '') === String(vq.calcType || '') && Number(c.quoteNum) === Number(vq.quoteNum)) return list[k];
        }
      }
    } catch (e) {}
    /* The highlighted pill is the only authoritative statement of what is on screen. */
    var pill = document.querySelector('.qr-quote-pill.is-active');
    var label = pill ? String(pill.textContent || '').trim() : '';
    if (label) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].quote.quoteName || '').trim() === label) return list[i];
      }
    }
    var pool = list.filter(function (r) { return r.isActive; });
    if (!pool.length) pool = list;
    pool.sort(function (a, b) {
      return (Date.parse(b.quote.generatedAt) || 0) - (Date.parse(a.quote.generatedAt) || 0);
    });
    return pool[0];
  }

  function writeQuote(ref) {
    try {
      if (ref.idx < 0) { localStorage.setItem(ref.key, JSON.stringify(ref.quote)); return; }
      var arr = JSON.parse(localStorage.getItem(ref.key) || '[]');
      arr[ref.idx] = ref.quote;
      localStorage.setItem(ref.key, JSON.stringify(arr));
    } catch (e) {}
  }

  function lineKey(b, i) {
    return [b.section || '', b.category || '', b.item || '', b.sku || '', i].join('|');
  }

  function ensureBaseline(q) {
    if (!q.cfBaseline) {
      q.cfBaseline = {};
      for (var i = 0; i < q.breakdown.length; i++) q.cfBaseline[lineKey(q.breakdown[i], i)] = Number(q.breakdown[i].quantity) || 0;
    }
    return q.cfBaseline;
  }

  function recomputeAdjustments(q) {
    var base = ensureBaseline(q), list = [];
    for (var i = 0; i < q.breakdown.length; i++) {
      var b = q.breakdown[i], k = lineKey(b, i);
      var from = base[k], to = Number(b.quantity) || 0;
      if (from === undefined || from === to) continue;
      list.push({ item: b.item, sku: b.sku || '', variantId: b.variantId || null, section: b.section || '',
                  from: from, to: to, delta: to - from,
                  action: to === 0 ? 'removed' : (to > from ? 'added' : 'reduced') });
    }
    q.cfAdjustments = list;
    return list;
  }

  function recomputeTotals(q) {
    var total = 0;
    for (var i = 0; i < q.breakdown.length; i++) {
      var b = q.breakdown[i];
      b.totalPrice = Math.round((Number(b.quantity) || 0) * (Number(b.unitPrice) || 0) * 100) / 100;
      total += b.totalPrice;
    }
    q.grandTotal = Math.round(total * 100) / 100;
    return q.grandTotal;
  }

  function summarise(adj) {
    return adj.map(function (a) {
      if (a.action === 'removed') return 'removed ' + a.from + ' x ' + a.item;
      if (a.action === 'added') return 'added ' + a.delta + ' x ' + a.item;
      return a.item + ' ' + a.from + ' to ' + a.to;
    }).join('; ');
  }

  function rewriteItems(items, st) {
    var wanted = {}, byId = {}, i;
    for (i = 0; i < st.quote.breakdown.length; i++) {
      var b = st.quote.breakdown[i];
      if (!b.variantId) continue;
      var id = String(b.variantId);
      wanted[id] = (wanted[id] || 0) + (Number(b.quantity) || 0);
    }
    for (i = 0; i < st.adj.length; i++) if (st.adj[i].variantId) byId[String(st.adj[i].variantId)] = st.adj[i];
    var removedNote = st.adj.filter(function (a) { return a.action === 'removed'; })
      .map(function (a) { return a.from + ' x ' + a.item; }).join('; ');
    var out = [], stamped = false;
    for (i = 0; i < items.length; i++) {
      var it = items[i], id2 = String(it.id);
      var qty = wanted.hasOwnProperty(id2) ? wanted[id2] : it.quantity;
      if (!qty || qty <= 0) continue;
      var copy = Object.assign({}, it, { quantity: qty });
      var a2 = byId[id2];
      if (a2) copy.properties = Object.assign({}, it.properties || {}, { 'Adjusted by customer': a2.from + ' to ' + a2.to });
      if (!stamped && removedNote) {
        copy.properties = Object.assign({}, copy.properties || {}, { 'Customer removed from quote': removedNote });
        stamped = true;
      }
      out.push(copy);
    }
    return out;
  }

  function installCartHook(getState) {
    if (window.__cfQuoteEditorCartHook) return;
    window.__cfQuoteEditorCartHook = true;
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    window.fetch = function (input, init) {
      try {
        var url = String((input && input.url) || input || '');
        if (/\/cart\/add(\.js)?/.test(url) && init && init.body) {
          var st = getState();
          if (st && st.adj && st.adj.length) {
            var payload = JSON.parse(String(init.body));
            if (payload && payload.items) {
              payload.items = rewriteItems(payload.items, st);
              init = Object.assign({}, init, { body: JSON.stringify(payload) });
            }
          }
        }
      } catch (e) {}
      return origFetch.call(this, input, init);
    };
  }

  var CSS = '.cf-qe{margin:18px 0;border:1px solid #d7dee7;border-radius:10px;overflow:hidden;font-size:.94rem;background:#fff}'
    + '.cf-qe-head{background:#f4f7fa;padding:11px 14px;font-weight:600;display:flex;justify-content:space-between;align-items:center;gap:12px}'
    + '.cf-qe-row{display:flex;align-items:center;gap:10px;padding:9px 14px;border-top:1px solid #eef2f6}'
    + '.cf-qe-row.cf-gone .cf-qe-name{opacity:.45;text-decoration:line-through}'
    + '.cf-qe-name{flex:1;min-width:0}'
    + '.cf-qe-sub{display:block;font-size:.78rem;color:#64748b;margin-top:1px}'
    + '.cf-qe-price{width:96px;text-align:right;font-variant-numeric:tabular-nums}'
    + '.cf-qe-ctl{display:flex;align-items:center;gap:5px}'
    + '.cf-qe-ctl button{width:29px;height:29px;border:1px solid #cbd5e1;background:#fff;border-radius:6px;cursor:pointer;font-size:15px;line-height:1;color:#0f172a}'
    + '.cf-qe-ctl button:hover{background:#eef2f6}'
    + '.cf-qe-ctl input{width:46px;height:29px;text-align:center;border:1px solid #cbd5e1;border-radius:6px;font-size:.9rem}'
    + '.cf-qe-foot{padding:11px 14px;border-top:1px solid #e2e8f0;background:#fbfdff;display:flex;justify-content:space-between;align-items:center;gap:12px}'
    + '.cf-qe-total{font-weight:700;font-size:1.05rem}'
    + '.cf-qe-note{margin:0;padding:10px 14px;background:#fff8e6;border-top:1px solid #f0e0b0;color:#7a5c00;font-size:.86rem}'
    + '.cf-qe-reset{background:none;border:none;color:#2563eb;cursor:pointer;font-size:.86rem;padding:0}'
    + '.cf-qe-tag{font-size:.72rem;background:#e2e8f0;color:#475569;border-radius:4px;padding:1px 5px;margin-left:6px}';

  function injectCss() {
    if (document.getElementById('cf-qe-css')) return;
    var s = document.createElement('style');
    s.id = 'cf-qe-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function build(ref) {
    var q = ref.quote;
    ensureBaseline(q);
    var wrap = document.createElement('div');
    wrap.className = 'cf-qe';
    wrap.innerHTML = '<div class="cf-qe-head"><span>Adjust your kit</span>'
      + '<button type="button" class="cf-qe-reset">Reset to recommended</button></div>'
      + '<div class="cf-qe-body"></div>'
      + '<div class="cf-qe-foot"><span>Adjusted total</span><span class="cf-qe-total"></span></div>';
    var body = wrap.querySelector('.cf-qe-body');

    /* 2026-09-07 (Pete): "in this adjust your kit section its too long. put all the parts
     * together that match to shorten it and make it easier to see what is in the kit."
     *
     * The breakdown carries ONE ROW PER SECTION, so a four-section job listed In-Ground
     * Post three times (7, 2, 4) and Flat Top Panel 3000mm twice. Same part, same price,
     * several rows. Grouping is display-only: q.breakdown is left exactly as it is, so the
     * per-section FULL BREAKDOWN above still reads correctly and nothing downstream that
     * relies on section attribution changes.
     *
     * A row now owns a LIST of breakdown indices (data-idx). Quantity and price are summed
     * across them; an edit is distributed back over them by fillFrom() below.
     *
     * Rows only merge when they are genuinely the same purchase - same SKU, same variant
     * and the SAME UNIT PRICE. Price is part of the key on purpose: the same SKU can appear
     * at two prices (a raked post has been seen at both $59.59 and $65.55), and summing
     * those into one row would misstate the money.
     */
    var groups = [], byKey = {};
    q.breakdown.forEach(function (b, i) {
      if (/delivery|pickup/i.test(b.category || '') || /delivery|pickup/i.test(b.item || '')) return;
      /* Glass emits its own "GST (10%)" line. It is derived, not a thing to buy. */
      if (/^tax$/i.test(String(b.category || '').trim()) || /\bgst\b/i.test(String(b.item || ''))) return;
      var key = [b.sku || '', b.item || '', b.unitPrice, b.variantId || ''].join('|');
      if (byKey[key] === undefined) { byKey[key] = groups.length; groups.push({ key: key, idx: [], b: b }); }
      groups[byKey[key]].idx.push(i);
    });

    groups.forEach(function (grp) {
      var b = grp.b;
      var row = document.createElement('div');
      row.className = 'cf-qe-row';
      row.setAttribute('data-idx', grp.idx.join(','));
      row.innerHTML = '<span class="cf-qe-name"></span>'
        + '<span class="cf-qe-ctl"><button type="button" data-d="-1" aria-label="Remove one">-</button>'
        + '<input type="number" min="0" step="1">'
        + '<button type="button" data-d="1" aria-label="Add one">+</button></span>'
        + '<span class="cf-qe-price"></span>';
      var name = row.querySelector('.cf-qe-name');
      name.textContent = b.item || '(item)';
      var sub = document.createElement('span');
      sub.className = 'cf-qe-sub';
      sub.textContent = [b.category || '', b.sku ? 'SKU ' + b.sku : '', MONEY(b.unitPrice) + ' each'].filter(Boolean).join(' - ');
      name.appendChild(sub);
      var resolvable = b.variantId
        || (b.sku && /^\d{12,}$/.test(String(b.sku)))
        || (b.sku && typeof window.CF_VARIANT === 'function' && window.CF_VARIANT(String(b.sku)));
      if (!resolvable) {
        var tag = document.createElement('span');
        tag.className = 'cf-qe-tag';
        tag.textContent = 'quoted only';
        tag.title = 'Priced on the quote; not sold as a separate cart line.';
        name.appendChild(tag);
      }
      var qty0 = 0;
      grp.idx.forEach(function (i) { qty0 += Number(q.breakdown[i].quantity) || 0; });
      row.querySelector('input').value = String(qty0);
      body.appendChild(row);
    });

    var note = document.createElement('p');
    note.className = 'cf-qe-note';
    note.style.display = 'none';
    wrap.appendChild(note);

    function idxOf(row) {
      return String(row.getAttribute('data-idx') || '').split(',')
        .filter(function (x) { return x !== ''; }).map(Number);
    }

    /* Spread a new total back over the rows the group came from. Each row is refilled up
     * to the quantity it was originally solved with, in order, so per-section amounts stay
     * as close to the engineered design as the new total allows; anything above the
     * original total lands on the first row. Reducing empties the later rows first. */
    function fillFrom(idx, target) {
      var base = q.cfBaseline || {};
      var left = Math.max(0, Math.floor(Number(target) || 0));
      idx.forEach(function (i) {
        var want = Number(base[lineKey(q.breakdown[i], i)]);
        if (!isFinite(want)) want = Number(q.breakdown[i].quantity) || 0;
        var give = Math.min(want, left);
        q.breakdown[i].quantity = give;
        left -= give;
      });
      if (left > 0 && idx.length) q.breakdown[idx[0]].quantity += left;
    }

    function refresh() {
      recomputeTotals(q);
      var adj = recomputeAdjustments(q);
      [].forEach.call(body.querySelectorAll('.cf-qe-row'), function (row) {
        var idx = idxOf(row), qty = 0, price = 0;
        idx.forEach(function (i) {
          var b = q.breakdown[i];
          if (!b) return;
          qty += Number(b.quantity) || 0;
          price += Number(b.totalPrice) || 0;
        });
        row.querySelector('input').value = String(qty);
        row.querySelector('.cf-qe-price').textContent = MONEY(price);
        if (qty === 0) row.classList.add('cf-gone'); else row.classList.remove('cf-gone');
      });
      wrap.querySelector('.cf-qe-total').textContent = MONEY(q.grandTotal);
      if (adj.length) {
        note.style.display = '';
        note.textContent = 'You changed ' + adj.length + ' item' + (adj.length > 1 ? 's' : '') + ': ' + summarise(adj) + '. These changes are recorded on your quote.';
      } else { note.style.display = 'none'; }
      writeQuote(ref);
      window.__cfQuoteState = { quote: q, adj: adj };
      try { document.dispatchEvent(new CustomEvent('cf:quote-adjusted', { detail: { adjustments: adj, total: q.grandTotal } })); } catch (e) {}
    }

    function setQty(idx, v) {
      if (!idx || !idx.length) return;
      fillFrom(idx, v);
      refresh();
    }

    function groupQty(idx) {
      var t = 0;
      idx.forEach(function (i) { if (q.breakdown[i]) t += Number(q.breakdown[i].quantity) || 0; });
      return t;
    }

    wrap.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest('button') : null;
      if (!btn) return;
      if (btn.className.indexOf('cf-qe-reset') >= 0) {
        var base = q.cfBaseline || {};
        q.breakdown.forEach(function (b, i) { var k = lineKey(b, i); if (base[k] !== undefined) b.quantity = base[k]; });
        refresh();
        return;
      }
      var d = btn.getAttribute('data-d');
      if (!d) return;
      var row = btn.closest('.cf-qe-row');
      if (!row) return;
      var idx = idxOf(row);
      setQty(idx, groupQty(idx) + Number(d));
    });

    wrap.addEventListener('change', function (ev) {
      if (!ev.target || ev.target.tagName !== 'INPUT') return;
      var row = ev.target.closest('.cf-qe-row');
      if (row) setQty(idxOf(row), ev.target.value);
    });

    refresh();
    return wrap;
  }

  function anchor() {
    var sel = ['#quote-summary', '.quote-summary', '#quote-totals', '.quote-total', '#quote-breakdown', '.quote-breakdown'];
    for (var i = 0; i < sel.length; i++) {
      var el = document.querySelector(sel[i]);
      if (el && el.parentNode) return el;
    }
    var hs = document.querySelectorAll('h2,h3,h4');
    for (var j = 0; j < hs.length; j++) if (/summary|total/i.test(hs[j].textContent || '')) return hs[j];
    return null;
  }

  var tries = 0;
  function tryInstall() {
    if (tries++ > 60) return;
    try {
      if (!/quote-review/.test(location.pathname)) return;
      if (document.querySelector('.cf-qe')) return;
      var ref = readQuote();
      if (!ref) { setTimeout(tryInstall, 500); return; }
      var host = anchor();
      if (!host) { setTimeout(tryInstall, 500); return; }
      injectCss();
      var ui = build(ref);
      host.parentNode.insertBefore(ui, host);
      installCartHook(function () { return window.__cfQuoteState; });
      console.log('[cf-quote-editor] installed - ' + ref.calc + ' ' + (ref.quote.quoteName || '') + ', ' + ref.quote.breakdown.length + ' lines');
    } catch (e) {
      console.warn('[cf-quote-editor] install failed', e);
      setTimeout(tryInstall, 800);
    }
  }

  /* quote-review.js swaps the displayed quote when a pill is clicked. Without this the
     editor keeps showing - and writing to - the quote it bound to on load, so the customer
     edits one quote while looking at another. */
  function rebuild() {
    try {
      var ex = document.querySelector('.cf-qe');
      if (ex && ex.parentNode) ex.parentNode.removeChild(ex);
      window.__cfQuoteState = null;
      tries = 0;
      tryInstall();
    } catch (e) {}
  }

  document.addEventListener('click', function (ev) {
    var t = ev.target;
    var pill = t && t.closest ? t.closest('.qr-quote-pill') : null;
    if (!pill) return;
    setTimeout(rebuild, 400);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tryInstall);
  else tryInstall();
  setTimeout(tryInstall, 1200);
  setTimeout(tryInstall, 3000);
})();
